import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBook,
  FiBookOpen,
  FiFileText,
  FiHeadphones,
  FiEdit3,
  FiMic,
  FiTarget,
  FiAward,
  FiTrendingUp,
  FiLayers,
  FiGrid,
  FiList,
  FiSearch,
  FiFilter,
  FiCheckCircle,
  FiClock,
  FiPlay,
  FiChevronRight,
  FiChevronDown,
  FiStar,
  FiZap,
  FiBarChart2,
  FiHash,
  FiPercent,
  FiBox,
  FiTriangle,
  FiCircle,
  FiSquare,
  FiActivity,
  FiPieChart,
  FiSliders,
  FiCpu,
  FiHelpCircle,
  FiBookmark,
  FiVolume2,
  FiGlobe,
  FiMap,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import { getFlashcardCount as apiGetFlashcardCount } from '../exams/gre/api/flashcardAPI';
import { getAccountScopedLocalStorageKey } from '../../utils/utils';
import './LearningDirectory.css';

// ============================================
// RESOURCE DEFINITIONS
// ============================================

// GRE Quant Concepts
const GRE_QUANT_CONCEPTS = [
  {
    id: 'integers',
    name: 'Integers & Number Properties',
    path: '/exams/gre/concepts/integers',
    icon: FiHash,
    difficulty: 'Foundational',
  },
  {
    id: 'percentage-ratio',
    name: 'Percentages & Ratios',
    path: '/exams/gre/concepts/percentage-ratio',
    icon: FiPercent,
    difficulty: 'Foundational',
  },
  {
    id: 'exponents',
    name: 'Exponents & Roots',
    path: '/exams/gre/concepts/exponents',
    icon: FiTrendingUp,
    difficulty: 'Foundational',
  },
  {
    id: 'algebra',
    name: 'Algebra',
    path: '/exams/gre/concepts/algebra',
    icon: FiSliders,
    difficulty: 'Intermediate',
  },
  {
    id: 'functions',
    name: 'Functions',
    path: '/exams/gre/concepts/functions',
    icon: FiCpu,
    difficulty: 'Intermediate',
  },
  {
    id: 'sets',
    name: 'Sets & Venn Diagrams',
    path: '/exams/gre/concepts/sets',
    icon: FiLayers,
    difficulty: 'Intermediate',
  },
  {
    id: 'probabilities',
    name: 'Probability & Counting',
    path: '/exams/gre/concepts/probabilities',
    icon: FiPieChart,
    difficulty: 'Advanced',
  },
  {
    id: 'work-rate',
    name: 'Work & Rate Problems',
    path: '/exams/gre/concepts/work-rate',
    icon: FiActivity,
    difficulty: 'Intermediate',
  },
  {
    id: 'distance-rate',
    name: 'Distance, Rate & Time',
    path: '/exams/gre/concepts/distance-rate',
    icon: FiClock,
    difficulty: 'Intermediate',
  },
  {
    id: 'coordinate-geometry',
    name: 'Coordinate Geometry',
    path: '/exams/gre/concepts/coordinate-geometry',
    icon: FiGrid,
    difficulty: 'Intermediate',
  },
  {
    id: 'circles',
    name: 'Circles',
    path: '/exams/gre/concepts/circles',
    icon: FiCircle,
    difficulty: 'Intermediate',
  },
  {
    id: 'triangles',
    name: 'Triangles',
    path: '/exams/gre/concepts/triangles',
    icon: FiTriangle,
    difficulty: 'Intermediate',
  },
  {
    id: 'quadrilaterals',
    name: 'Quadrilaterals',
    path: '/exams/gre/concepts/quadrilaterals',
    icon: FiSquare,
    difficulty: 'Intermediate',
  },
  {
    id: 'polygons',
    name: 'Polygons',
    path: '/exams/gre/concepts/polygons',
    icon: FiLayers,
    difficulty: 'Intermediate',
  },
  {
    id: 'three-dimensional-figures',
    name: '3D Figures',
    path: '/exams/gre/concepts/three-dimensional-figures',
    icon: FiBox,
    difficulty: 'Advanced',
  },
  {
    id: 'volumes',
    name: 'Volume & Surface Area',
    path: '/exams/gre/concepts/volumes',
    icon: FiBox,
    difficulty: 'Intermediate',
  },
  {
    id: 'statistics',
    name: 'Statistics & Data Analysis',
    path: '/exams/gre/concepts/statistics',
    icon: FiBarChart2,
    difficulty: 'Intermediate',
  },
  {
    id: 'graphs',
    name: 'Graph Interpretation',
    path: '/exams/gre/concepts/graphs',
    icon: FiPieChart,
    difficulty: 'Intermediate',
  },
  {
    id: 'math-shortcuts',
    name: 'Math Shortcuts & Tricks',
    path: '/exams/gre/concepts/math-shortcuts',
    icon: FiZap,
    difficulty: 'Strategy',
  },
];

// GRE Reading Comprehension Lessons
const GRE_RC_LESSONS = [
  {
    id: 'question-types',
    name: 'Question Types Overview',
    path: '/exams/gre/reading-comprehension/lesson/question-types',
    icon: FiHelpCircle,
  },
  {
    id: 'five-step-process',
    name: 'Five-Step Process',
    path: '/exams/gre/reading-comprehension/lesson/five-step-process',
    icon: FiTarget,
  },
  {
    id: 'passage-components',
    name: 'Passage Components',
    path: '/exams/gre/reading-comprehension/lesson/passage-components',
    icon: FiFileText,
  },
  {
    id: 'chunk-reading',
    name: 'Chunk Reading Strategy',
    path: '/exams/gre/reading-comprehension/lesson/chunk-reading',
    icon: FiLayers,
  },
  {
    id: 'speed-reading',
    name: 'Speed Reading Techniques',
    path: '/exams/gre/reading-comprehension/lesson/speed-reading',
    icon: FiZap,
  },
  {
    id: 'trap-answers',
    name: 'Avoiding Trap Answers',
    path: '/exams/gre/reading-comprehension/lesson/trap-answers',
    icon: FiTarget,
  },
  {
    id: 'master-passage',
    name: 'Master Passage Method',
    path: '/exams/gre/reading-comprehension/lesson/master-passage',
    icon: FiAward,
  },
];

// GRE Verbal Exercises
const GRE_VERBAL_EXERCISES = [
  {
    id: 'text-completion',
    name: 'Text Completion Practice',
    path: '/exams/gre/verbal/vocabulary/text-completion',
    icon: FiEdit3,
    type: 'exercise',
  },
  {
    id: 'sentence-equivalence',
    name: 'Sentence Equivalence Practice',
    path: '/exams/gre/verbal/vocabulary/sentence-equivalence',
    icon: FiFileText,
    type: 'exercise',
  },
  {
    id: 'reading-comprehension',
    name: 'Reading Comprehension',
    path: '/exams/gre/reading-comprehension',
    icon: FiBookOpen,
    type: 'exercise',
  },
];

// GRE Vocabulary Resources
const GRE_VOCABULARY = [
  {
    id: 'vocabulary-mastery',
    name: 'Vocabulary Mastery (Articles)',
    path: '/exams/gre/verbal/vocabulary',
    icon: FiBook,
    description: 'Daily articles with vocabulary',
  },
  {
    id: 'vocabulary-book',
    name: 'GRE Word List (800+ words)',
    path: '/exams/gre/verbal/vocabulary-book',
    icon: FiBookOpen,
    description: 'Complete GRE vocabulary',
  },
  {
    id: 'flashcards',
    name: 'Flashcard Book',
    path: '/exams/gre/verbal/vocabulary/flashcards',
    icon: FiBookmark,
    description: 'Your saved flashcards',
  },
  {
    id: 'saved-concepts',
    name: 'Saved Concepts',
    path: '/exams/gre/concepts/saved',
    icon: FiStar,
    description: 'Bookmarked quant concepts',
  },
];

// GRE Full Tests
const GRE_TESTS = [
  {
    id: 'full-test-1',
    name: 'Full Test #1',
    path: '/exams/gre/full/full-test-1',
    icon: FiFileText,
    duration: '3h 45m',
  },
  {
    id: 'full-test-2',
    name: 'Full Test #2',
    path: '/exams/gre/full/full-test-2',
    icon: FiFileText,
    duration: '3h 45m',
  },
  {
    id: 'full-test-3',
    name: 'Full Test #3',
    path: '/exams/gre/full/full-test-3',
    icon: FiFileText,
    duration: '3h 45m',
  },
];

// IELTS Grammar Lessons
const IELTS_GRAMMAR = [
  {
    id: 'sentence-structure',
    name: 'Sentence Structure',
    path: '/exams/english/grammar/sentence-structure',
    level: 'Beginner',
  },
  {
    id: 'subject-verb-agreement',
    name: 'Subject-Verb Agreement',
    path: '/exams/english/grammar/subject-verb-agreement',
    level: 'Beginner',
  },
  {
    id: 'gerunds',
    name: 'Gerunds',
    path: '/exams/english/grammar/gerunds',
    level: 'Elementary',
  },
  {
    id: 'infinitives',
    name: 'Infinitives',
    path: '/exams/english/grammar/infinitives',
    level: 'Elementary',
  },
  {
    id: 'gerund-infinitive',
    name: 'Gerunds vs Infinitives',
    path: '/exams/english/grammar/gerund-infinitive',
    level: 'Elementary',
  },
  {
    id: 'sentence-clauses',
    name: 'Sentence Clauses',
    path: '/exams/english/grammar/sentence-clauses',
    level: 'Intermediate',
  },
  {
    id: 'passive-voice',
    name: 'Passive Voice',
    path: '/exams/english/grammar/passive-voice',
    level: 'Intermediate',
  },
  {
    id: 'modal-verbs',
    name: 'Modal Verbs',
    path: '/exams/english/grammar/modal-verbs',
    level: 'Intermediate',
  },
  {
    id: 'relative-clauses',
    name: 'Relative Clauses',
    path: '/exams/english/grammar/relative-clauses',
    level: 'Upper-Intermediate',
  },
  {
    id: 'noun-clauses',
    name: 'Noun Clauses',
    path: '/exams/english/grammar/noun-clauses',
    level: 'Upper-Intermediate',
  },
  {
    id: 'adverbial-clauses',
    name: 'Adverbial Clauses',
    path: '/exams/english/grammar/adverbial-clauses',
    level: 'Upper-Intermediate',
  },
  {
    id: 'complex-sentences',
    name: 'Complex Sentences',
    path: '/exams/english/grammar/complex-sentences',
    level: 'Advanced',
  },
  {
    id: 'parallel-structure',
    name: 'Parallel Structure',
    path: '/exams/english/grammar/parallel-structure',
    level: 'Advanced',
  },
  {
    id: 'grammar-rules',
    name: 'Advanced Grammar Rules',
    path: '/exams/english/grammar/grammar-rules',
    level: 'Advanced',
  },
];

// IELTS Vocabulary Lessons
const IELTS_VOCABULARY = [
  {
    id: 'essential-words',
    name: 'Essential Words',
    path: '/exams/english/vocabulary/essential-words',
    level: 'Beginner',
  },
  {
    id: 'spelling-us-uk',
    name: 'US vs UK Spelling',
    path: '/exams/english/vocabulary/spelling-us-uk',
    level: 'Beginner',
  },
  {
    id: 'uncountable-nouns',
    name: 'Uncountable Nouns',
    path: '/exams/english/vocabulary/uncountable-nouns',
    level: 'Elementary',
  },
  {
    id: 'individual-words',
    name: 'Individual Word Study',
    path: '/exams/english/vocabulary/individual-words',
    level: 'Elementary',
  },
  {
    id: 'collocations',
    name: 'Collocations',
    path: '/exams/english/vocabulary/collocations',
    level: 'Intermediate',
  },
  {
    id: 'phrasal-verbs',
    name: 'Phrasal Verbs',
    path: '/exams/english/vocabulary/phrasal-verbs',
    level: 'Intermediate',
  },
  {
    id: 'idioms',
    name: 'Idioms & Expressions',
    path: '/exams/english/vocabulary/idioms',
    level: 'Upper-Intermediate',
  },
  {
    id: 'topic-related',
    name: 'Topic-Related Vocabulary',
    path: '/exams/english/vocabulary/topic-related',
    level: 'Upper-Intermediate',
  },
  {
    id: 'paraphrasing',
    name: 'Paraphrasing Techniques',
    path: '/exams/english/vocabulary/paraphrasing',
    level: 'Advanced',
  },
  {
    id: 'vocabulary-for-writing',
    name: 'Vocabulary for Writing',
    path: '/exams/english/vocabulary/vocabulary-for-writing',
    level: 'Advanced',
  },
];

// IELTS Skill Courses
const IELTS_LISTENING_COURSES = [
  {
    id: 'listening-strategies',
    name: 'Listening Strategies',
    path: '/exams/english/courses/listening-strategies',
  },
  {
    id: 'note-taking',
    name: 'Note-Taking Skills',
    path: '/exams/english/courses/note-taking',
  },
  {
    id: 'listening-details',
    name: 'Listening for Details',
    path: '/exams/english/courses/listening-details',
  },
  {
    id: 'accent-training',
    name: 'Accent Training',
    path: '/exams/english/courses/accent-training',
  },
];

const IELTS_READING_COURSES = [
  {
    id: 'skimming-scanning',
    name: 'Skimming & Scanning',
    path: '/exams/english/courses/skimming-scanning',
  },
  {
    id: 'reading-strategies',
    name: 'Reading Strategies',
    path: '/exams/english/courses/reading-strategies',
  },
  {
    id: 'reading-time',
    name: 'Time Management',
    path: '/exams/english/courses/reading-time',
  },
  {
    id: 'academic-vocab',
    name: 'Academic Vocabulary',
    path: '/exams/english/courses/academic-vocab',
  },
];

const IELTS_WRITING_COURSES = [
  {
    id: 'task1-overview',
    name: 'Task 1 Overview',
    path: '/exams/english/courses/task1-overview',
  },
  {
    id: 'essay-structure',
    name: 'Essay Structure',
    path: '/exams/english/courses/essay-structure',
  },
  {
    id: 'coherence-cohesion',
    name: 'Coherence & Cohesion',
    path: '/exams/english/courses/coherence-cohesion',
  },
  {
    id: 'band-descriptors',
    name: 'Band Descriptors',
    path: '/exams/english/courses/band-descriptors',
  },
];

const IELTS_SPEAKING_COURSES = [
  {
    id: 'fluency',
    name: 'Fluency Training',
    path: '/exams/english/courses/fluency',
  },
  {
    id: 'pronunciation',
    name: 'Pronunciation',
    path: '/exams/english/courses/pronunciation',
  },
  {
    id: 'speaking-part2',
    name: 'Part 2 Long Turn',
    path: '/exams/english/courses/speaking-part2',
  },
  {
    id: 'speaking-part3',
    name: 'Part 3 Discussion',
    path: '/exams/english/courses/speaking-part3',
  },
];

// IELTS Tests
const IELTS_TESTS = [
  {
    id: 'listening',
    name: 'Listening Test',
    path: '/exams/english/listening',
    icon: FiHeadphones,
    duration: '40 min',
  },
  {
    id: 'reading',
    name: 'Reading Test',
    path: '/exams/english/reading',
    icon: FiBookOpen,
    duration: '60 min',
  },
  {
    id: 'writing',
    name: 'Writing Test',
    path: '/exams/english/writing',
    icon: FiEdit3,
    duration: '60 min',
  },
  {
    id: 'speaking',
    name: 'Speaking Test',
    path: '/exams/english/speaking',
    icon: FiMic,
    duration: '11-14 min',
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const getCompletionData = () => {
  try {
    const storageKey = getAccountScopedLocalStorageKey('lesson_completion_data');
    const data = storageKey ? localStorage.getItem(storageKey) : null;
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

const getArticleProgress = (userEmail) => {
  try {
    const key = `liwaza_article_progress_${userEmail}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : { articlesRead: [], totalArticles: 0 };
  } catch {
    return { articlesRead: [], totalArticles: 0 };
  }
};

const getFlashcardCountFromCache = (userEmail) => {
  try {
    const key = `gre_flashcards_${userEmail}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data).length : 0;
  } catch {
    return 0;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const LearningDirectory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('all'); // 'all', 'gre', 'ielts'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [expandedCategories, setExpandedCategories] = useState({
    'gre-quant': true,
    'gre-verbal': true,
    'gre-vocabulary': true,
    'gre-tests': false,
    'ielts-grammar': true,
    'ielts-vocabulary': true,
    'ielts-courses': true,
    'ielts-tests': false,
  });
  const [completionData, setCompletionData] = useState({});
  const [articleProgress, setArticleProgress] = useState({});
  const [flashcardCount, setFlashcardCount] = useState(0);

  const userEmail = (() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const raw =
        user?.email ||
        user?.user_email ||
        user?.userEmail ||
        localStorage.getItem('userEmail');
      const normalized = String(raw || '')
        .trim()
        .toLowerCase();
      if (!normalized || normalized === 'undefined' || normalized === 'null')
        return 'default';
      return normalized;
    } catch {
      const raw = localStorage.getItem('userEmail');
      const normalized = String(raw || '')
        .trim()
        .toLowerCase();
      if (!normalized || normalized === 'undefined' || normalized === 'null')
        return 'default';
      return normalized;
    }
  })();

  useEffect(() => {
    setCompletionData(getCompletionData());
    setArticleProgress(getArticleProgress(userEmail));
    setFlashcardCount(getFlashcardCountFromCache(userEmail));
    apiGetFlashcardCount(userEmail).then((count) => setFlashcardCount(count));
  }, [userEmail]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Check if a lesson is completed
  const isCompleted = (lessonId) => {
    return completionData[lessonId]?.completed === true;
  };

  // Filter resources based on search
  const filterResources = (resources) => {
    if (!searchQuery.trim()) return resources;
    const query = searchQuery.toLowerCase();
    return resources.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        r.id.toLowerCase().includes(query) ||
        (r.description && r.description.toLowerCase().includes(query))
    );
  };

  // Navigate to resource
  const handleNavigate = (path, state = {}) => {
    navigate(path, { state });
  };

  // Render a resource card
  const renderResourceCard = (resource, type = 'lesson') => {
    const Icon = resource.icon || FiBook;
    const completed = isCompleted(resource.id);

    return (
      <div
        key={resource.id}
        className={`directory-resource-card ${completed ? 'completed' : ''} ${viewMode}`}
        onClick={() => handleNavigate(resource.path)}
      >
        <div
          className="resource-card-icon"
          style={{ background: completed ? '#4CAF50' : undefined }}
        >
          {completed ? <FiCheckCircle /> : <Icon />}
        </div>
        <div className="resource-card-content">
          <h4>{resource.name}</h4>
          {resource.description && (
            <p className="resource-description">{resource.description}</p>
          )}
          {resource.difficulty && (
            <span
              className={`difficulty-badge ${resource.difficulty.toLowerCase()}`}
            >
              {resource.difficulty}
            </span>
          )}
          {resource.level && (
            <span
              className={`level-badge ${resource.level.toLowerCase().replace('-', '')}`}
            >
              {resource.level}
            </span>
          )}
          {resource.duration && (
            <span className="duration-badge">
              <FiClock /> {resource.duration}
            </span>
          )}
          {resource.type === 'exercise' && (
            <span className="type-badge exercise">
              <FiPlay /> Practice
            </span>
          )}
        </div>
        <FiChevronRight className="resource-card-arrow" />
      </div>
    );
  };

  // Render a category section
  const renderCategory = (categoryId, title, icon, resources, color) => {
    const Icon = icon;
    const filteredResources = filterResources(resources);
    const completedCount = filteredResources.filter((r) =>
      isCompleted(r.id)
    ).length;
    const isExpanded = expandedCategories[categoryId];

    if (filteredResources.length === 0 && searchQuery) return null;

    return (
      <div
        key={categoryId}
        className={`directory-category ${isExpanded ? 'expanded' : ''}`}
      >
        <div
          className="category-header"
          onClick={() => toggleCategory(categoryId)}
          style={{ '--category-color': color }}
        >
          <div className="category-header-left">
            <div className="category-icon" style={{ background: color }}>
              <Icon />
            </div>
            <div className="category-info">
              <h3>{title}</h3>
              <span className="category-count">
                {completedCount}/{filteredResources.length} completed
              </span>
            </div>
          </div>
          <div className="category-header-right">
            <div className="category-progress">
              <div
                className="category-progress-bar"
                style={{
                  width: `${(completedCount / filteredResources.length) * 100}%`,
                  background: color,
                }}
              />
            </div>
            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
          </div>
        </div>

        {isExpanded && (
          <div className={`category-content ${viewMode}`}>
            {filteredResources.map((resource) => renderResourceCard(resource))}
          </div>
        )}
      </div>
    );
  };

  // Count total resources and completed
  const totalStats = useMemo(() => {
    const allResources = [
      ...GRE_QUANT_CONCEPTS,
      ...GRE_RC_LESSONS,
      ...GRE_VERBAL_EXERCISES,
      ...GRE_VOCABULARY,
      ...IELTS_GRAMMAR,
      ...IELTS_VOCABULARY,
      ...IELTS_LISTENING_COURSES,
      ...IELTS_READING_COURSES,
      ...IELTS_WRITING_COURSES,
      ...IELTS_SPEAKING_COURSES,
    ];

    const completed = allResources.filter((r) => isCompleted(r.id)).length;

    return {
      total: allResources.length,
      completed,
      articlesRead: articleProgress.articlesRead?.length || 0,
      flashcards: flashcardCount,
    };
  }, [completionData, articleProgress, flashcardCount]);

  return (
    <div className="layout">
      <Sidebar />
      <main className="content">
        <div className="learning-directory">
          {/* Header */}
          <div className="directory-header">
            <div className="directory-header-content">
              <div className="directory-title">
                <FiBook className="directory-icon" />
                <div>
                  <h1>Learning Directory</h1>
                  <p>All your courses, lessons, and exercises in one place</p>
                </div>
              </div>

              {/* Stats */}
              <div className="directory-stats">
                <div className="stat-item">
                  <span className="stat-value">{totalStats.completed}</span>
                  <span className="stat-label">Lessons Done</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{totalStats.articlesRead}</span>
                  <span className="stat-label">Articles Read</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{totalStats.flashcards}</span>
                  <span className="stat-label">Flashcards</span>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="directory-controls">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Search courses, lessons, exercises..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="filter-controls">
              <div className="section-tabs">
                <button
                  className={activeSection === 'all' ? 'active' : ''}
                  onClick={() => setActiveSection('all')}
                >
                  All
                </button>
                <button
                  className={activeSection === 'gre' ? 'active' : ''}
                  onClick={() => setActiveSection('gre')}
                >
                  <FiTarget /> GRE
                </button>
                <button
                  className={activeSection === 'ielts' ? 'active' : ''}
                  onClick={() => setActiveSection('ielts')}
                >
                  <FiGlobe /> IELTS
                </button>
              </div>

              <div className="view-toggle">
                <button
                  className={viewMode === 'grid' ? 'active' : ''}
                  onClick={() => setViewMode('grid')}
                >
                  <FiGrid />
                </button>
                <button
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => setViewMode('list')}
                >
                  <FiList />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="directory-content">
            {/* GRE Section */}
            {(activeSection === 'all' || activeSection === 'gre') && (
              <div className="directory-section">
                <div className="section-header">
                  <FiTarget className="section-icon" />
                  <h2>GRE Preparation</h2>
                </div>

                {renderCategory(
                  'gre-quant',
                  'Quantitative Concepts',
                  FiBarChart2,
                  GRE_QUANT_CONCEPTS,
                  '#6366f1'
                )}
                {renderCategory(
                  'gre-verbal',
                  'Verbal Exercises',
                  FiEdit3,
                  GRE_VERBAL_EXERCISES,
                  '#8b5cf6'
                )}
                {renderCategory(
                  'gre-rc',
                  'Reading Comprehension Lessons',
                  FiBookOpen,
                  GRE_RC_LESSONS,
                  '#a855f7'
                )}
                {renderCategory(
                  'gre-vocabulary',
                  'Vocabulary Resources',
                  FiBook,
                  GRE_VOCABULARY,
                  '#d946ef'
                )}
                {renderCategory(
                  'gre-tests',
                  'Full Practice Tests',
                  FiFileText,
                  GRE_TESTS,
                  '#f43f5e'
                )}

                {/* Quick Access */}
                <div className="quick-access-section">
                  <h3>
                    <FiZap /> Quick Access
                  </h3>
                  <div className="quick-access-cards">
                    <div
                      className="quick-access-card"
                      onClick={() =>
                        handleNavigate('/exams/gre', {
                          activeCategory: 'vocabularymastery',
                        })
                      }
                    >
                      <FiBook />
                      <span>Daily Articles</span>
                    </div>
                    <div
                      className="quick-access-card"
                      onClick={() =>
                        handleNavigate(
                          '/exams/gre/verbal/vocabulary/flashcards'
                        )
                      }
                    >
                      <FiBookmark />
                      <span>My Flashcards</span>
                    </div>
                    <div
                      className="quick-access-card"
                      onClick={() =>
                        handleNavigate('/exams/gre', {
                          activeCategory: 'concepts',
                        })
                      }
                    >
                      <FiPlay />
                      <span>Quant Practice</span>
                    </div>
                    <div
                      className="quick-access-card"
                      onClick={() =>
                        handleNavigate('/exams/gre/quant/progress')
                      }
                    >
                      <FiTrendingUp />
                      <span>Quant Progress</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* IELTS Section */}
            {(activeSection === 'all' || activeSection === 'ielts') && (
              <div className="directory-section">
                <div className="section-header">
                  <FiGlobe className="section-icon" />
                  <h2>IELTS Preparation</h2>
                </div>

                {renderCategory(
                  'ielts-grammar',
                  'Grammar Lessons',
                  FiFileText,
                  IELTS_GRAMMAR,
                  '#10b981'
                )}
                {renderCategory(
                  'ielts-vocabulary',
                  'Vocabulary Lessons',
                  FiBook,
                  IELTS_VOCABULARY,
                  '#14b8a6'
                )}

                {/* Skill Courses */}
                <div className="subcategory-group">
                  <h3 className="subcategory-title">
                    <FiLayers /> Skill Courses
                  </h3>
                  {renderCategory(
                    'ielts-listening',
                    'Listening Courses',
                    FiHeadphones,
                    IELTS_LISTENING_COURSES,
                    '#06b6d4'
                  )}
                  {renderCategory(
                    'ielts-reading',
                    'Reading Courses',
                    FiBookOpen,
                    IELTS_READING_COURSES,
                    '#0ea5e9'
                  )}
                  {renderCategory(
                    'ielts-writing',
                    'Writing Courses',
                    FiEdit3,
                    IELTS_WRITING_COURSES,
                    '#3b82f6'
                  )}
                  {renderCategory(
                    'ielts-speaking',
                    'Speaking Courses',
                    FiMic,
                    IELTS_SPEAKING_COURSES,
                    '#6366f1'
                  )}
                </div>

                {renderCategory(
                  'ielts-tests',
                  'Practice Tests',
                  FiTarget,
                  IELTS_TESTS,
                  '#8b5cf6'
                )}

                {/* Journey Link */}
                <div
                  className="journey-link-card"
                  onClick={() => handleNavigate('/exams/english/journey')}
                >
                  <div className="journey-link-icon">
                    <FiMap />
                  </div>
                  <div className="journey-link-content">
                    <h3>60-Day English Journey</h3>
                    <p>Your personalized learning path with daily activities</p>
                  </div>
                  <FiChevronRight className="journey-link-arrow" />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LearningDirectory;
